import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Accommodation {
  id: string;
  producer_id: string;
  title: string;
  slug: string;
  description_short: string | null;
  description: string | null;
  status: string;
  beds: number | null;
  guest_capacity: number | null;
  price_per_night_text: string | null;
  booking_mode: string;
  booking_deadline_hours: number;
  check_in_time: string | null;
  check_out_time: string | null;
  amenities: string[] | null;
  house_rules: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

export function useProducerAccommodations(producerId: string | undefined) {
  return useQuery({
    queryKey: ['producer-accommodations', producerId],
    queryFn: async () => {
      if (!producerId) return [];
      const { data, error } = await supabase
        .from('producer_accommodations')
        .select('*')
        .eq('producer_id', producerId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Accommodation[];
    },
    enabled: !!producerId,
  });
}

export function useAccommodation(accommodationId: string | undefined) {
  return useQuery({
    queryKey: ['accommodation', accommodationId],
    queryFn: async () => {
      if (!accommodationId) return null;
      const { data, error } = await supabase
        .from('producer_accommodations')
        .select('*')
        .eq('id', accommodationId)
        .single();
      if (error) throw error;
      return data as Accommodation;
    },
    enabled: !!accommodationId,
  });
}

export function useUpsertAccommodation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: Partial<Accommodation> & { producer_id: string }) => {
      if (values.id) {
        const { id, created_at: _ca, ...rest } = values;
        const { data, error } = await supabase
          .from('producer_accommodations')
          .update({ ...rest, updated_at: new Date().toISOString() } as any)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('producer_accommodations')
          .insert(values as any)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['producer-accommodations', data.producer_id] });
      queryClient.invalidateQueries({ queryKey: ['accommodation', data.id] });
    },
  });
}

export function useAccommodationAvailabilityBlocks(accommodationId: string | undefined) {
  return useQuery({
    queryKey: ['accommodation-blocks', accommodationId],
    queryFn: async () => {
      if (!accommodationId) return [];
      const { data, error } = await supabase
        .from('accommodation_availability_blocks')
        .select('*')
        .eq('accommodation_id', accommodationId)
        .order('start_date', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!accommodationId,
  });
}

export function useAddManualBlock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { accommodation_id: string; start_date: string; end_date: string; note?: string }) => {
      const { data, error } = await supabase
        .rpc('add_manual_block_safe', {
          p_accommodation_id: values.accommodation_id,
          p_start_date: values.start_date,
          p_end_date: values.end_date,
          p_note: values.note ?? null,
        });
      if (error) {
        // Surface the Swedish error from the RPC
        throw new Error(error.message || 'Kunde inte lägga till blockering');
      }
      return { id: data, accommodation_id: values.accommodation_id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation-blocks', data.accommodation_id] });
    },
  });
}

export function useDeleteManualBlock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ blockId, accommodationId }: { blockId: string; accommodationId: string }) => {
      const { error } = await supabase
        .from('accommodation_availability_blocks')
        .delete()
        .eq('id', blockId);
      if (error) throw error;
      return accommodationId;
    },
    onSuccess: (accommodationId) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation-blocks', accommodationId] });
    },
  });
}

export function useAccommodationBookings(accommodationId: string | undefined) {
  return useQuery({
    queryKey: ['accommodation-bookings', accommodationId],
    queryFn: async () => {
      if (!accommodationId) return [];
      const { data, error } = await supabase
        .from('accommodation_bookings')
        .select('*')
        .eq('accommodation_id', accommodationId)
        .order('check_in', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!accommodationId,
  });
}

// ── Price Rules ──

export interface AccommodationPriceRule {
  id: string;
  accommodation_id: string;
  rule_type: 'season' | 'special';
  title: string;
  start_date: string;
  end_date: string;
  price_ore: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useAccommodationPriceRules(accommodationId: string | undefined) {
  return useQuery({
    queryKey: ['accommodation-price-rules', accommodationId],
    queryFn: async () => {
      if (!accommodationId) return [];
      const { data, error } = await supabase
        .from('accommodation_price_rules')
        .select('*')
        .eq('accommodation_id', accommodationId)
        .order('start_date', { ascending: true });
      if (error) throw error;
      return data as AccommodationPriceRule[];
    },
    enabled: !!accommodationId,
  });
}

export function useUpsertPriceRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (values: Partial<AccommodationPriceRule> & { accommodation_id: string }) => {
      if (values.id) {
        const { id, created_at: _ca, ...rest } = values;
        const { data, error } = await supabase
          .from('accommodation_price_rules')
          .update({ ...rest, updated_at: new Date().toISOString() } as any)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('accommodation_price_rules')
          .insert(values as any)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation-price-rules', data.accommodation_id] });
    },
  });
}

export function useDeletePriceRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ruleId, accommodationId }: { ruleId: string; accommodationId: string }) => {
      const { error } = await supabase
        .from('accommodation_price_rules')
        .delete()
        .eq('id', ruleId);
      if (error) throw error;
      return accommodationId;
    },
    onSuccess: (accommodationId) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation-price-rules', accommodationId] });
    },
  });
}
